import { CV_EDUCATION_LABELS, CV_EDUCATION_LEVELS, type CvAnalysis, type CvEducationLevel, type CvExperience, type JobStatus } from "@shared/modules/x-extraction";

/** Statuts vus depuis l'analyse : « à valider » y veut dire « analysé, prêt à devenir un dossier ». */
export const ANALYSIS_STATUS_LABELS: Record<JobStatus, string> = {
  en_attente: "En attente",
  en_cours: "Lecture en cours",
  a_valider: "Analysé",
  applique: "Dossier créé",
  rejete: "Écarté",
  echec: "Échec",
};

export const ANALYSIS_STATUS_VARIANT: Record<JobStatus, "neutral" | "primary" | "success" | "warning" | "danger" | "info"> = {
  en_attente: "neutral",
  en_cours: "primary",
  a_valider: "info",
  applique: "success",
  rejete: "neutral",
  echec: "danger",
};

export const METHOD_LABELS: Record<NonNullable<CvAnalysis["method"]>, string> = {
  "pdf-texte": "PDF (texte)",
  "pdf-ocr": "PDF numérisé (OCR)",
  "image-ocr": "image (OCR)",
  docx: "Word",
  texte: "texte",
};

export function warningLabel(w: string): string {
  if (w.startsWith("modele_indisponible")) return "modèle local indisponible : identité seule";
  if (w.startsWith("modele_en_erreur")) return "le modèle a échoué";
  if (w === "texte_insuffisant") return "peu de texte lisible";
  return w;
}

export function fullName(a: CvAnalysis): string {
  const n = [a.identity.firstName, a.identity.lastName].filter(Boolean).join(" ");
  return n || a.originalName || "Candidat sans nom";
}

/** 90 mois → « 7,5 ans » ; 8 mois → « 8 mois ». */
export function formatMonths(months: number | null | undefined): string {
  if (months === null || months === undefined) return "—";
  if (months < 12) return `${months} mois`;
  const y = Math.round(months / 1.2) / 10;
  return `${y.toLocaleString("fr-CA")} an${y >= 2 ? "s" : ""}`;
}

function shortDate(d: string | null): string {
  if (!d) return "?";
  const [y, m] = d.split("-");
  return m ? `${m}/${y}` : y!;
}

export function period(e: Pick<CvExperience, "start" | "end" | "current">): string {
  if (!e.start && !e.end && !e.current) return "";
  return `${shortDate(e.start)} – ${e.current ? "aujourd'hui" : shortDate(e.end)}`;
}

export function levelLabel(l: CvEducationLevel | null | undefined): string {
  return l ? CV_EDUCATION_LABELS[l] : "—";
}

function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

/** Recherche locale dans un rapport : tous les mots doivent apparaître (sans accents). */
export function matches(a: CvAnalysis, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const p = a.profile;
  const parts: (string | null | undefined)[] = [a.identity.firstName, a.identity.lastName, a.identity.email, a.originalName];
  if (p) {
    parts.push(p.title, p.summary, p.city, p.country, p.totals.lastTitle);
    for (const e of p.experiences) parts.push(e.title, e.employer, e.location, ...e.highlights);
    for (const e of p.education) parts.push(e.degree, e.field, e.school);
    parts.push(...p.skills.technical, ...p.skills.tools, ...p.skills.soft, ...p.licenses, ...p.strengths, ...p.languages.map((l) => l.language));
    parts.push(...p.certifications.map((c) => c.name));
  }
  const h = fold(parts.filter(Boolean).join(" "));
  return words.every((w) => h.includes(w));
}

/** Les compétences les plus parlantes d'un profil, pour une colonne de tableau. */
export function keySkills(a: CvAnalysis, n = 5): string[] {
  const p = a.profile;
  return p ? [...p.skills.technical, ...p.skills.tools].slice(0, n) : [];
}

export type Tally = [label: string, count: number][];

function tally(values: string[], limit: number): Tally {
  const counts = new Map<string, { label: string; n: number }>();
  for (const v of values) {
    const k = fold(v.trim());
    if (!k) continue;
    const cur = counts.get(k);
    if (cur) cur.n++;
    else counts.set(k, { label: v.trim(), n: 1 });
  }
  return [...counts.values()]
    .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label, "fr"))
    .slice(0, limit)
    .map((c) => [c.label, c.n]);
}

export interface LotStats {
  analysed: number;
  averageMonths: number | null;
  medianMonths: number | null;
  levels: Tally;
  languages: Tally;
  skills: Tally;
  titles: Tally;
}

/** Vue d'ensemble d'un lot : ce qui revient le plus, et l'expérience typique. */
export function lotStats(items: CvAnalysis[]): LotStats {
  const profiles = items.map((a) => a.profile).filter((p): p is NonNullable<typeof p> => !!p);
  const months = profiles.map((p) => p.totals.experienceMonths).filter((m): m is number => m !== null).sort((a, b) => a - b);
  const mid = Math.floor(months.length / 2);
  const levels = CV_EDUCATION_LEVELS.map((l) => [CV_EDUCATION_LABELS[l], profiles.filter((p) => p.totals.highestLevel === l).length] as [string, number]).filter(([, n]) => n > 0);
  return {
    analysed: profiles.length,
    averageMonths: months.length ? Math.round(months.reduce((a, b) => a + b, 0) / months.length) : null,
    medianMonths: months.length ? (months.length % 2 ? months[mid]! : Math.round((months[mid - 1]! + months[mid]!) / 2)) : null,
    levels,
    languages: tally(profiles.flatMap((p) => p.languages.map((l) => l.language)), 10),
    skills: tally(profiles.flatMap((p) => [...p.skills.technical, ...p.skills.tools]), 24),
    titles: tally(profiles.map((p) => p.title ?? p.totals.lastTitle ?? "").filter(Boolean), 10),
  };
}
